/**
 * Turns API error bodies into something a user can act on.
 *
 * The API answers validation failures in two different shapes, and both used to collapse into a
 * vague toast:
 *   1. Our own middleware  -> { message, errors: [{ property, message }] }
 *   2. ASP.NET model state -> { title: 'One or more validation errors occurred.',
 *                               errors: { 'lines[0].quantity': ['...'] } }
 * Shape 2 also carries framework wording ("The JSON value could not be converted to
 * System.Decimal") that means nothing outside the codebase, so it gets rewritten here.
 */

/** Model-state keys that only ever restate that binding failed; the sibling entry is the real error. */
const NOISE_KEYS = new Set(['', '$', 'command', 'request', 'body', 'dto']);

const FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  quantity: 'Quantity',
  unitprice: 'Unit price',
  estimatedunitprice: 'Estimated price',
  currency: 'Currency',
  locationid: 'Location',
  itemid: 'Item',
  uomid: 'Unit of measure',
  neededbydate: 'Needed by date',
  priority: 'Priority',
  notes: 'Notes',
  supplierid: 'Supplier',
  categoryid: 'Category',
  sku: 'SKU',
  name: 'Name',
  email: 'Email',
  expecteddate: 'Expected date',
  receivedquantity: 'Received quantity',
};

function labelForSegment(segment) {
  const key = segment.toLowerCase();
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // camelCase / PascalCase -> spaced words, e.g. "trackingReference" -> "Tracking reference"
  const spaced = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_.]+/g, ' ').trim();
  if (!spaced) return segment;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * `$.lines[0].quantity` -> `Line 1, Quantity`. Indexes are shifted to 1-based so they line up with
 * the rows the user is looking at.
 */
export function humanizeFieldPath(path) {
  if (!path) return '';
  const parts = [];
  const cleaned = String(path).replace(/^\$\.?/, '');
  for (const raw of cleaned.split('.')) {
    if (!raw) continue;
    const match = raw.match(/^([^[]+)\[(\d+)\]$/);
    if (match) {
      const collection = labelForSegment(match[1]).replace(/s$/i, '');
      parts.push(`${collection} ${Number(match[2]) + 1}`);
    } else {
      parts.push(labelForSegment(raw));
    }
  }
  return parts.join(', ');
}

/** Replaces framework wording with something a user can act on. */
function humanizeMessage(message, fieldLabel) {
  const text = String(message || '').trim();
  const subject = fieldLabel || 'This field';

  if (/could not be converted to .*(Decimal|Int32|Int64|Double)/i.test(text) || /is not a valid number/i.test(text))
    return `${subject} must be a number.`;
  if (/could not be converted to .*(DateTime|DateOnly)/i.test(text)) return `${subject} must be a valid date.`;
  if (/could not be converted to/i.test(text)) return `${subject} has an unexpected value.`;
  if (/^The \w+ field is required\.$/i.test(text)) return `${subject} is required.`;
  if (/must not be empty|cannot be empty/i.test(text) && fieldLabel) return `${subject} is required.`;

  return text;
}

/**
 * Normalizes any error body into `[{ property, label, message }]`.
 * Returns an empty array when the body carries no field-level detail.
 */
export function extractFieldErrors(body) {
  if (!body || typeof body !== 'object') return [];
  const raw = body.errors;
  const out = [];

  if (Array.isArray(raw)) {
    // Our middleware shape. These messages are authored for users and already name their subject
    // ("Line 2 needs a quantity…"), so they are shown verbatim.
    for (const entry of raw) {
      if (!entry) continue;
      if (typeof entry === 'string') {
        out.push({ property: '', label: '', message: entry, selfDescribing: true });
        continue;
      }
      const property = entry.property || entry.Property || '';
      out.push({
        property,
        label: humanizeFieldPath(property),
        message: entry.message || entry.Message || '',
        selfDescribing: true,
      });
    }
  } else if (raw && typeof raw === 'object') {
    // ASP.NET ValidationProblemDetails shape — terse framework wording that needs its field named.
    for (const [property, messages] of Object.entries(raw)) {
      if (NOISE_KEYS.has(property.toLowerCase())) continue;
      const label = humanizeFieldPath(property);
      const list = Array.isArray(messages) ? messages : [messages];
      for (const message of list) {
        const rewritten = humanizeMessage(message, label);
        out.push({
          property,
          label,
          message: rewritten,
          // humanizeMessage folds the field name in when it recognises the error, so prefixing
          // again would read "Quantity: Quantity must be a number."
          selfDescribing: Boolean(label) && rewritten.toLowerCase().startsWith(label.toLowerCase()),
        });
      }
    }
  }

  return out.filter((e) => e.message);
}

/**
 * Best readable message for an error body, preferring specific field errors over generic titles.
 */
export function buildErrorMessage(body, status) {
  if (typeof body === 'string' && body.trim()) return body.trim();

  const fields = extractFieldErrors(body);
  if (fields.length) {
    const lines = fields.map((e) =>
      (!e.label || e.selfDescribing ? e.message : `${e.label}: ${e.message}`));
    const unique = [...new Set(lines)];
    const shown = unique.slice(0, 4).join('\n');
    return unique.length > 4 ? `${shown}\n…and ${unique.length - 4} more.` : shown;
  }

  if (body && typeof body === 'object') {
    const generic = body.message || body.error || body.detail;
    if (generic) return String(generic);
    // `title` last: it is usually the unhelpful "One or more validation errors occurred."
    if (body.title) return String(body.title);
  }

  return `Request failed (${status ?? 'unknown'})`;
}
